import axios, { AxiosResponse } from "axios";

import {
	getRequest,
	postRequest,
	deleteRequest,
	putRequest
} from "@/Utils/Service/BackendService";
import { Document } from "@/Contracts/Documents";

import { DeletedRecord, DocumentPayment, Template } from "@/Contracts";
import { getQueryParamAuthDetails } from "@/Contracts/Utils";
import {
	BlockListResponse,
	BlockRequest,
	Block,
	CloneBlockRequest,
	BlockResponse,
	GOFPaymentSummaryRequestData,
	UpdatePageDetailsRequest
} from "@/Contracts/ProposalBuilder/Block";
import {
	concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse,
	concatBlocksAndDraftBlockInTemplateResponse,
	deepFreeze
} from "@/Utils";

const blockUrl = "/v2/blocks";
const getBlockListUrl =
	"/v2/blocks?document_id={document_id}&revision_id={revision_id}";
const getBlockDetailsUrl = "/v2/blocks/{block_id}";
const editBlockUrl = "/v2/blocks/{block_id}";
const editBlockFromClientUrl = "/v2/blocks/{blockId}/client";
const editBlockFromPublicClientUrl = "/v2/blocks/{blockId}/public-client";
const reorderChildrenBlocksUrl = "/v2/blocks/reorder/{block_id}";
const cloneBlockUrl = "/v2/blocks/clone";
const cloneBlockToSingleUrl = "/v2/blocks/clone-to-single-block";
const calculatePaymentSummaryUrl = "/v2/blocks/payment-details";
const updateDocumentPageDetailsUrl =
	"/v2/documents/update-page-details/{document_id}";
const updateTemplatePageDetailsUrl =
	"/v2/templates/update-page-details/{document_id}";

const addTinyMceEditorLoadsURL = "/v2/blocks/tinymce-load";

// get block
export const getBlock = async (blockId: string): Promise<BlockResponse> => {
	try {
		const response: AxiosResponse<BlockResponse> = await axios(
			getRequest(
				getBlockDetailsUrl.replace("{block_id}", blockId),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// get blocks by documentId
export const getBlockList = async (
	documentId: string,
	revisionId: string,
	limit: number | null,
	lastBlocKId: string | null
): Promise<BlockListResponse> => {
	try {
		let url = getBlockListUrl
			.replace("{document_id}", documentId)
			.replace("{revision_id}", revisionId);
		url += limit ? `&limit=${limit}` : "";
		url += lastBlocKId ? `&last_block_id=${lastBlocKId}` : "";
		const response: AxiosResponse<BlockListResponse> = await axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// add a new block
export const addBlock = async (
	addBlockRequest: BlockRequest
): Promise<BlockResponse> => {
	try {
		const response: AxiosResponse<BlockResponse> = await axios(
			postRequest(blockUrl, addBlockRequest, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// update block
export const editBlock = async (block: Block): Promise<BlockResponse> => {
	try {
		const response: AxiosResponse<BlockResponse> = await axios(
			putRequest(
				editBlockUrl.replace("{block_id}", block.id),
				block,
				true,
				false,
				false
			)
		);
		return deepFreeze(response.data);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editQuoteBlock = async (block: Block): Promise<BlockResponse> => {
	return editBlock(block);
};

// reorder children blocks
export const reorderChildrenBlock = async (block: Block): Promise<Block> => {
	try {
		const response: AxiosResponse<Block> = await axios(
			putRequest(
				reorderChildrenBlocksUrl.replace("{block_id}", block.id),
				{
					children_blocks: block.children_blocks
				},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// delete block
export const deleteBlock = async (blockId: string): Promise<DeletedRecord> => {
	try {
		const response: AxiosResponse<DeletedRecord> = await axios(
			deleteRequest(`${blockUrl}/${blockId}`, null, true, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// update block from client
export const editBlockFromClient = async (
	block: Block
): Promise<BlockResponse> => {
	try {
		// grab client id
		const queryParamAuthDetails = getQueryParamAuthDetails();
		const queryParam = queryParamAuthDetails
			? "?" + queryParamAuthDetails.queryParam + "&"
			: "";
		const response: AxiosResponse<BlockResponse> = await axios(
			putRequest(
				editBlockFromClientUrl.replace("{blockId}", block.id) +
					queryParam,
				block,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// update block from public client - generic order form case
export const editBlockFromPublicClient = async (
	block: Block,
	companyId?: string
): Promise<BlockResponse> => {
	try {
		const queryParam = companyId
			? `?company_id=${companyId}&is_commit=false`
			: "";
		const response: AxiosResponse<BlockResponse> = await axios(
			putRequest(
				editBlockFromPublicClientUrl.replace("{blockId}", block.id) +
					queryParam,
				block,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const calculatePaymentSummary = async (
	reuquestData: GOFPaymentSummaryRequestData,
	companyId?: string
) => {
	try {
		const queryParam = companyId ? `?company_id=${companyId}` : "";
		const response: AxiosResponse<DocumentPayment> = await axios(
			postRequest(
				calculatePaymentSummaryUrl + queryParam,
				reuquestData,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// clone a block
export const cloneBlock = async (
	cloneBlockRequest: CloneBlockRequest
): Promise<BlockResponse> => {
	try {
		const response: AxiosResponse<BlockResponse> = await axios(
			postRequest(cloneBlockUrl, cloneBlockRequest, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

// clone a block to single text block
export const cloneBlockToSingle = async (
	cloneBlockRequest: CloneBlockRequest
): Promise<BlockResponse> => {
	try {
		const response: AxiosResponse<BlockResponse> = await axios(
			postRequest(
				cloneBlockToSingleUrl,
				cloneBlockRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateDocumentPageDetails = async (
	payload: UpdatePageDetailsRequest,
	documentId: string
): Promise<Document> => {
	try {
		const response: AxiosResponse<Document> = await axios(
			putRequest(
				updateDocumentPageDetailsUrl.replace(
					"{document_id}",
					documentId
				),
				payload,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockAndFilterDraftSignersInDocumentResponse(
			response.data
		);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateTemplatePageDetails = async (
	payload: UpdatePageDetailsRequest,
	documentId: string
): Promise<Template> => {
	try {
		const response: AxiosResponse<Template> = await axios(
			putRequest(
				updateTemplatePageDetailsUrl.replace(
					"{document_id}",
					documentId
				),
				payload,
				true,
				false,
				false
			)
		);
		return concatBlocksAndDraftBlockInTemplateResponse(response.data);
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addTinyMceEditorLoads = async (
	documentId: string,
	blockId: string
) => {
	await axios(
		postRequest(
			addTinyMceEditorLoadsURL,
			{ documentId, blockId },
			true,
			false,
			false
		)
	);
};
